import CheckboxIcon from '@icons/Checkbox'
import CheckboxCheckedIcon from '@icons/CheckboxChecked'
import CheckboxIndeterminateIcon from '@icons/CheckboxIndeterminate'
import RadioCheckedIcon from '@icons/RadioCheckedIcon'
import RadioIcon from '@icons/RadioIcon'
import { alpha, type ThemeOptions } from '@mui/material/styles'
import { createElement } from 'react'

import type { ThemeColors } from '../types'

export const controlComponents = (colors: ThemeColors): ThemeOptions['components'] => ({
  MuiCheckbox: {
    defaultProps: {
      checkedIcon: createElement(CheckboxCheckedIcon),
      color: 'primary',
      disableRipple: true,
      icon: createElement(CheckboxIcon),
      indeterminateIcon: createElement(CheckboxIndeterminateIcon),
    },
    styleOverrides: {
      colorPrimary: {
        '&&.Mui-checked:hover': {
          backgroundColor: 'transparent',
        },
      },
      indeterminate: {
        color: colors.active[1],
      },
      root: {
        '& > span': {
          '&::before': {
            background: colors.active[5],
            borderRadius: 2,
            content: "''",
            height: 20,
            left: 0,
            position: 'absolute',
            top: 0,
            transform: 'scale(0)',
            width: 20,
          },
          position: 'relative',
        },
        '& input': {
          height: 48,
          left: -14,
          top: -14,
          width: 48,
        },
        '& svg': {
          height: 20,
          width: 20,
          zIndex: 0,
        },
        // Keyboard focus keeps the halo but adds a ring, so it never reads as
        // a plain hover.
        '&.Mui-focusVisible > span::before': {
          boxShadow: `0 0 0 3px ${alpha(colors.active[1], 0.26)}`,
        },
        '&:hover, &.Mui-focusVisible': {
          '& > span::before': {
            animation: `200ms alternate hoverControlStart,
          600ms infinite alternate 200ms hoverControl`,
          },
          background: 'transparent',
          color: colors.active[1],
        },
        background: 'transparent',
        border: 'transparent',
        borderRadius: 3,
        padding: 14,
      },
    },
  },
  MuiRadio: {
    defaultProps: {
      checkedIcon: createElement(RadioCheckedIcon),
      color: 'primary',
      disableRipple: true,
      icon: createElement(RadioIcon),
    },
    styleOverrides: {
      colorPrimary: {
        '&&.Mui-checked:hover': {
          backgroundColor: 'transparent',
        },
      },
      root: {
        '& > span': {
          '&::before': {
            background: colors.active[5],
            borderRadius: '100%',
            content: "''",
            height: 20,
            left: 0,
            position: 'absolute',
            top: 0,
            transform: 'scale(0)',
            width: 20,
          },
          position: 'relative',
        },
        '& input': {
          height: 48,
          left: -14,
          top: -14,
          width: 48,
        },
        '& svg': {
          height: 20,
          width: 20,
          zIndex: 0,
        },
        // Keyboard focus keeps the halo but adds a ring, so it never reads as
        // a plain hover.
        '&.Mui-focusVisible > span::before': {
          boxShadow: `0 0 0 3px ${alpha(colors.active[1], 0.26)}`,
        },
        '&:hover, &.Mui-focusVisible': {
          '& > span::before': {
            animation: `200ms alternate hoverControlStart,
          600ms infinite alternate 200ms hoverControl`,
          },
          background: 'transparent',
          color: colors.active[1],
        },
        background: 'transparent',
        border: 'transparent',
        borderRadius: '100%',
        padding: 14,
      },
    },
  },
  MuiSwitch: {
    defaultProps: {
      color: 'primary',
      disableRipple: true,
    },
    styleOverrides: {
      // `$rule` references are JSS; under emotion they matched nothing, which
      // is why the disabled switch used to render identically to an enabled one.
      colorPrimary: {
        '&.Mui-checked': {
          '& + .MuiSwitch-track': {
            borderColor: colors.active[1],
          },
          '&:hover': {
            backgroundColor: 'transparent',
          },
          color: colors.background.paper,
        },
        '&.Mui-disabled': {
          '& + .MuiSwitch-track': {
            backgroundColor: 'transparent',
            border: `2px solid ${colors.disabled}`,
          },
          '&.Mui-checked': {
            color: colors.background.paper,
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: colors.disabled,
            borderColor: colors.disabled,
          },
          color: colors.disabled,
        },
        color: colors.main[3],
      },
      root: {
        // The root is a plain span, so keyboard focus has to be observed on the
        // input with `:focus-within`. Focus gets a static ring, hover the pulse.
        '&:focus-within .MuiSwitch-switchBase:not(.Mui-disabled) + .MuiSwitch-track': {
          borderColor: colors.active[1],
          boxShadow: `0 0 0 3px ${alpha(colors.active[1], 0.26)}`,
        },
        '&:hover .MuiSwitch-switchBase:not(.Mui-disabled) + .MuiSwitch-track': {
          animation: `200ms alternate hoverSwitchStart,
              600ms infinite alternate 200ms hoverSwitch`,
        },
        height: 48,
        padding: '12px 16px',
        width: 80,
      },
      sizeSmall: {
        '& .MuiSwitch-switchBase': {
          '&.Mui-checked': {
            transform: 'translateX(15px)',
          },
          left: 2,
          marginLeft: 1,
          top: 4,
        },
        '& .MuiSwitch-thumb': {
          height: 12,
          left: 3,
          width: 12,
        },
        '& .MuiSwitch-track': {
          borderRadius: 10,
          height: 16,
          width: 32,
        },
        height: 'auto',
        padding: '6px 8px',
        width: 'auto',
      },
      switchBase: {
        '&.Mui-checked': {
          transform: 'translateX(23px)',
        },
        '&:hover, &.Mui-focusVisible': {
          backgroundColor: 'transparent',
        },
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
        color: colors.background.paper,
        left: 4,
        marginLeft: 4,
        top: 8,
        // Thumb lands with a small spring
        transition: 'transform 320ms cubic-bezier(0.3, 1.55, 0.55, 1)',
      },
      thumb: {
        boxShadow: 'none',
        height: 14,
        left: 4,
        position: 'relative',
        width: 14,
      },
      track: {
        backgroundColor: 'transparent',
        border: `2px solid ${colors.main[3]}`,
        borderRadius: 12,
        height: 24,
        // This track is an outline, not a fill, so MUI's opacity dimming (0.5
        // checked, 0.12/0.2 disabled) would just fade it away — state is
        // carried by the border and fill colours above instead. The old
        // `[['1'], '!important']` array was JSS and serialised to nothing.
        opacity: '1 !important' as any,
        transition: '200ms',
        width: 48,
      },
    },
  },
})
